import { type FC, useState } from "react";
import { type AddressButtonProps, type AddressDetails } from "./types";
import {
  saveAddress,
  type AddressData,
} from "../../../services/addresses";
import { Button } from "flowbite-react";
import { getWorkerIdForUser, updateWorkerLocation } from "../../../services/workers";

interface EditHouseNumberFormProps {
  baseNumber: string;
  currentSuffix: string;
  onSave: (newNumber: string) => void;
  onCancel: () => void;
}

const EditHouseNumberForm: FC<EditHouseNumberFormProps> = ({
  baseNumber,
  currentSuffix,
  onSave,
  onCancel,
}) => {
  const [suffix, setSuffix] = useState(currentSuffix);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(baseNumber + suffix);
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center space-x-2">
      <span className="text-sm text-gray-600 dark:text-gray-300">
        {baseNumber}
      </span>
      <div className="flex flex-col">
        <label htmlFor="suffix" className="sr-only">
          House number suffix
        </label>
        <input
          id="suffix"
          type="text"
          value={suffix}
          onChange={(e) => setSuffix(e.target.value)}
          className="w-12 rounded-lg border border-gray-300 bg-gray-50 p-1 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          maxLength={3}
          placeholder="a"
          aria-label="House number suffix"
        />
      </div>
      <Button type="submit" size="xs" color="primary">
        Save
      </Button>
      <Button type="button" size="xs" color="gray" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
};

export const EditHouseNumberButton: FC<AddressButtonProps> = ({
  tempAddress,
  user,
  onAddressUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async (newHouseNumber: string) => {
    if (!tempAddress || !user?.id) return;

    setIsLoading(true);
    try {
      // Create new address object with modified number
      const newAddress: AddressData = {
        street: tempAddress.street,
        house_number: newHouseNumber,
        zip_code: tempAddress.zip_code,
        city: tempAddress.city,
        country: tempAddress.country,
        latitude: tempAddress.latitude,
        longitude: tempAddress.longitude,
        created_by_user_id: user.id,
      };

      // Use saveAddress function to either get existing or create new
      const savedAddress = await saveAddress(newAddress);

      // Update tempAddress with saved address data
      if (savedAddress) {
        const updatedAddress: AddressDetails = {
          id: savedAddress.id,
          street: newAddress.street,
          house_number: newAddress.house_number,
          zip_code: newAddress.zip_code,
          city: newAddress.city,
          country: newAddress.country,
          latitude: newAddress.latitude,
          longitude: newAddress.longitude,
        };
        onAddressUpdate(updatedAddress);

        // Update worker location with new address coordinates
        try {
          const workerId = await getWorkerIdForUser(user.id);
          await updateWorkerLocation(
            workerId,
            newAddress.latitude.toString(),
            newAddress.longitude.toString()
          );
          console.log("Worker location updated successfully");
        } catch (error) {
          console.error("Error updating worker location:", error);
          // Don't show error to user since the main operation succeeded
        }
      }
    } catch (error) {
      console.error("Error handling house number change:", error);
    } finally {
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  if (!tempAddress) return null;

  // Extract base number and suffix
  const match = tempAddress.house_number.match(/(\d+)([a-zA-Z]*)$/);
  if (!match) return null;

  const baseNumber = match[1] || "";
  const currentSuffix = match[2] || "";

  if (isEditing) {
    return (
      <EditHouseNumberForm
        baseNumber={baseNumber}
        currentSuffix={currentSuffix}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <button
      onClick={handleEditClick}
      className="rounded-lg bg-primary-100 px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-200 dark:bg-primary-700 dark:text-white dark:hover:bg-primary-600"
      title="Edit house number suffix"
      disabled={isLoading || !user?.id}
    >
      {isLoading ? "..." : "Edit"}
    </button>
  );
};
